import * as Sentry from '@sentry/react';
import {useQuery} from '@tanstack/react-query';
import React from 'react';
import {StringParam, useQueryParam} from 'use-query-params';

import ErrorPage from 'app/components/Error/ErrorPage';
import Loading from 'app/components/Loading';
import PublicStory, {StorySharePayload} from 'app/components/PublicStory';
import {api} from 'app/modules/Remote';
import {ApiPublicLensResponse} from 'app/modules/Remote/api';
import {ApiResponseError} from 'app/utils/apiUtils';

/**
 * The page served at /p/s/:uuid. Loads the share link's payload — the same `/p/<uuid>`
 * response a report link renders from — and hands it to the story renderer. A link without
 * a report, or one made for the document page, reads as unavailable: a link opens only as
 * the presentation it was made for.
 */
const PublicStoryPage: React.FunctionComponent<{uuid: string}> = ({uuid}) => {
  const isEmbedded = useQueryParam('embedded', StringParam)[0] === 'true';

  const {
    data: payload,
    error,
    isPending,
  } = useQuery<ApiPublicLensResponse, ApiResponseError>({
    queryKey: ['public-story', uuid],
    queryFn: async () => {
      const response = await api.public(uuid);
      return response.get('data').toJS() as ApiPublicLensResponse;
    },
    retry: false,
  });

  React.useEffect(() => {
    if (isServerError(error)) {
      Sentry.captureException(error.error);
    }
  }, [error]);

  if (isPending) {
    return <Loading />;
  }

  if (!isStoryPayload(payload)) {
    return (
      <ErrorPage
        message={
          isServerError(error)
            ? 'Something went wrong. Please wait a moment, then reload the page.'
            : 'This content is no longer available.'
        }
        linkTo={isEmbedded ? null : 'https://www.upstream.tech/lens'}
      />
    );
  }

  return <PublicStory uuid={uuid} payload={payload} isEmbedded={isEmbedded} />;
};

// 4XX means the link is gone or was never one; 5XX is ours to investigate.
const isServerError = (error: ApiResponseError | null): error is ApiResponseError =>
  (error?.status ?? 0) >= 500;

const isStoryPayload = (payload: ApiPublicLensResponse | undefined): payload is StorySharePayload =>
  payload?.report?.presentation === 'story';

export default PublicStoryPage;
